import { posDb } from '@/db/pos-database';
import { decryptManyFromStorage } from '@/db/encrypted-access';
import { useShiftStore } from '@/stores/use-shift-store';
import { getUnsyncedCount, retryDeadEntries } from '@/services/sync-queue.service';
import { isUploadableParkedDraft, pushLocalParkedBills } from '@/services/parked-bills.service';

/**
 * G2.27 — Pre-logout guard.
 *
 * Because we wipe IndexedDB on logout (purge-and-resync rotation strategy),
 * any rows in these "unsynced work" tables would be lost:
 *
 *   - `pending_sync`     offline bills / stock adjustments not yet pushed
 *   - `local_quotes`     quotations created offline, not yet acknowledged
 *   - `draft_bills`      in-progress carts that hold items, and return /
 *                        quotation carts parked on this till only
 *                        (an empty cart's draft holds nothing to lose — T2-012)
 *   - `parked_bills`     sale carts parked offline, not yet uploaded to the
 *                        store's server list (gst-parked 120) — these block
 *                        logout like unsynced bills; parks already on the
 *                        server belong to the store and survive logout
 *
 * We count them and let the caller decide whether to block logout, prompt
 * the user, or force a final sync attempt.
 */

export interface UnsyncedWorkSummary {
  pending_sync: number;
  local_quotes: number;
  draft_bills: number;
  /** gst-parked 120: parked sale carts on this till still waiting to upload. */
  parked_bills: number;
  open_shift: boolean;
  total: number;
}

export async function getUnsyncedWorkSummary(): Promise<UnsyncedWorkSummary> {
  const [pending, quotes, draftRows] = await Promise.all([
    // T17: DEAD and CONFLICT rows are unsynced too — a wipe loses them just the same.
    getUnsyncedCount(),
    posDb.local_quotes.where('is_synced').equals(0).count(),
    posDb.draft_bills.toArray().then(decryptManyFromStorage),
  ]);
  const parked = draftRows.filter(isUploadableParkedDraft).length;
  const drafts = draftRows.filter(
    (d) => !isUploadableParkedDraft(d) && (d.is_parked === 1 || (d.items?.length ?? 0) > 0),
  ).length;
  const openShift = useShiftStore.getState().activeAttendance != null;
  return {
    pending_sync: pending,
    local_quotes: quotes,
    draft_bills: drafts,
    parked_bills: parked,
    open_shift: openShift,
    total: pending + quotes + drafts + parked + (openShift ? 1 : 0),
  };
}

/** Convenience flag: is the device currently online? */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

export interface LogoutGuardDecision {
  /** True iff logout is allowed to proceed without user confirmation. */
  canLogout: boolean;
  /** Populated when `canLogout === false` so the UI can render a dialog. */
  reason?: {
    kind: 'unsynced-bills' | 'offline-with-unsynced' | 'online-with-unsynced' | 'open-shift';
    summary: UnsyncedWorkSummary;
  };
}

/**
 * Evaluate whether logout should proceed silently or ask the user.
 *
 * Policy:
 *   - Any row still in `pending_sync` (PENDING / PROCESSING / FAILED / DEAD /
 *     CONFLICT) → BLOCK, online or not, and `force` cannot override it (T17,
 *     owner decision — Odoo: a session cannot close with unsynced orders).
 *     Online, one push is tried first.
 *   - Any parked sale cart not yet uploaded (gst-parked 120) → BLOCK the same
 *     way. Online, the upload is tried first; a park the server refused stays
 *     until the cashier resumes or discards it.
 *   - `force` → proceed (the user chose to discard the rest below).
 *   - No unsynced work → proceed silently.
 *   - Online + unsynced work → proceed silently (sync-queue will flush on
 *     its regular interval before the user is fully signed out; the brief
 *     window is acceptable because online sync typically completes in
 *     under a second).
 *   - Offline + unsynced work → BLOCK. The UI must show a dialog with
 *     "Cancel" or "Discard and logout" (explicit user choice).
 *
 * The caller (auth store logout action) is responsible for rendering the
 * dialog and re-invoking logout with a `force` flag when the user chooses
 * "Discard and logout".
 */
export async function evaluateLogoutGuard(
  opts: { force?: boolean } = {},
): Promise<LogoutGuardDecision> {
  let summary = await getUnsyncedWorkSummary();
  if ((summary.pending_sync > 0 || summary.parked_bills > 0) && isOnline()) {
    if (summary.pending_sync > 0) await retryDeadEntries();
    if (summary.parked_bills > 0) await pushLocalParkedBills().catch(() => undefined);
    summary = await getUnsyncedWorkSummary();
  }
  if (summary.pending_sync > 0 || summary.parked_bills > 0) {
    return {
      canLogout: false,
      reason: { kind: 'unsynced-bills', summary },
    };
  }
  if (opts.force) return { canLogout: true };
  // Open shift always forces a confirm — otherwise the server-side attendance
  // row stays stuck and blocks the next clock-in.
  if (summary.open_shift) {
    return {
      canLogout: false,
      reason: { kind: 'open-shift', summary },
    };
  }
  const offlineUnsynced = summary.pending_sync + summary.local_quotes + summary.draft_bills;
  if (offlineUnsynced === 0) {
    return { canLogout: true };
  }
  if (isOnline()) {
    return { canLogout: true };
  }
  return {
    canLogout: false,
    reason: { kind: 'offline-with-unsynced', summary },
  };
}
